import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";

import EcoIcon from "../eco-icon/EcoIcon";
import { uploadImageApi } from "../../views/supervisor/supervisorApi";

import "./CaptureImage.css";

interface CaptureImageProps {
  token: string;
  label: string;
  onUploadSuccess: (assetId: string) => void;
  onClear?: () => void;
  /** Texto do botão que envia a foto tirada (padrão: "Usar esta foto"). */
  confirmLabel?: string;
}

/**
 * Câmera do app: foto na hora, sem galeria (redesign de 15/10/2026). Sem câmera
 * liberada no navegador, abre a câmera nativa do celular pelo `<input capture>`.
 */
export default function CaptureImage({ token, label, onUploadSuccess, onClear, confirmLabel }: CaptureImageProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedAssetId, setUploadedAssetId] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const startCamera = async () => {
    try {
      // Câmera traseira.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 640 },
          height: { ideal: 640 },
        },
      });
      streamRef.current = stream;
      setCameraActive(true);
      setPreviewUrl(null);
      setSelectedFile(null);
      setUploadedAssetId(null);

      // No próximo ciclo o <video> já está na tela.
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch((err) => {
            console.error("Erro ao iniciar reprodução:", err);
          });
        }
      }, 50);
    } catch (err: any) {
      console.warn("WebRTC camera not available, falling back to native capture input", err);
      fileInputRef.current?.click();
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !streamRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");

    // Recorte quadrado no centro do vídeo.
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      const sx = (video.videoWidth - size) / 2;
      const sy = (video.videoHeight - size) / 2;
      ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(blob));
          }
        },
        "image/jpeg",
        0.9,
      );
    }

    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadedAssetId(null);
    }
  };

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      setUploading(true);
      const res = await uploadImageApi({ token, file: selectedFile });
      setUploadedAssetId(res.assetId);
      toast.success("Foto enviada com sucesso!");
      onUploadSuccess(res.assetId);
    } catch (err: any) {
      toast.error(err.message || "Erro ao fazer upload da imagem.");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadedAssetId(null);
    stopCamera();
    if (onClear) onClear();
  };

  return (
    <div className="ecoCapture">
      {label ? <p className="ecoCapture__label">{label}</p> : null}

      {/* Câmera nativa, quando a do navegador não abre. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="ecoCapture__file"
        tabIndex={-1}
        aria-hidden="true"
      />

      {cameraActive && (
        <>
          <div className="ecoCapture__frame">
            <video ref={videoRef} autoPlay playsInline muted className="ecoCapture__media" />
          </div>
          <div className="ecoCapture__actions">
            <button type="button" className="ecoBtn" onClick={stopCamera}>
              Cancelar
            </button>
            <button type="button" className="ecoBtn ecoBtn--primary" onClick={capturePhoto}>
              <EcoIcon name="camera" size={18} />
              Tirar foto
            </button>
          </div>
        </>
      )}

      {!cameraActive && !previewUrl && (
        <button type="button" className="ecoCapture__start" onClick={startCamera}>
          <span className="ecoCapture__startIcon" aria-hidden="true">
            <EcoIcon name="camera" size={32} strokeWidth={1.8} />
          </span>
          <span className="ecoCapture__startTitle">Abrir a câmera</span>
          <span className="ecoCapture__startHint">Foto na hora, sem galeria</span>
        </button>
      )}

      {!cameraActive && previewUrl && (
        <>
          <div className="ecoCapture__frame">
            <img src={previewUrl} alt="Foto tirada" className="ecoCapture__media" />
            {uploadedAssetId ? (
              <span className="ecoCapture__badge">
                <EcoIcon name="check" size={14} strokeWidth={3} />
                Foto enviada
              </span>
            ) : null}
          </div>
          <div className="ecoCapture__actions">
            <button type="button" className="ecoBtn" onClick={handleClear} disabled={uploading}>
              <EcoIcon name="refresh" size={18} />
              Tirar outra
            </button>
            {!uploadedAssetId ? (
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? "Enviando..." : confirmLabel ?? "Usar esta foto"}
              </button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
